import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Socio } from "../types/Socio";
import socioService from "../services/socioService.tsx";
import {
  CategoriaItem,
  asignarSocioACategoria,
  obtenerCategorias,
  obtenerCategoriasDeSocio,
  quitarSocioDeCategoria,
} from "../services/categoriasService.ts";
import { Rubro, getNombreRubro, getRubrosIds, getRubrosNombres } from "../enums/Rubro.tsx";
import { getNombreSegmento, Segmento } from "../enums/Segmento.tsx";
import { TipoSocioNombre } from "../enums/TipoSocio.tsx";
import {
  User,
  Mail,
  CreditCard,
  MapPin,
  Phone,
  Hash,
  Flag,
  Briefcase,
  FileText,
  Layers,
  Calendar,
  Building,
  CheckSquare,
  Tag,
  Loader2,
  ArrowLeft,
  CircleHelp,
  ImagePlus,
} from "lucide-react";
import Loading from "./Loading.tsx";

type FieldProps = {
  name: keyof Socio | string;
  label: string;
  icon: React.ElementType;
  type?: string;
  placeholder?: string;
  highlighted?: boolean;
  tooltip?: string;
};

const inputBase =
  "mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none";

const Section: React.FC<{ title: string; description?: string }> = ({
  title,
  description,
  children,
}) => (
  <div className="space-y-4 rounded-2xl border border-slate-100 bg-slate-50/70 p-5 shadow-sm">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs uppercase tracking-[0.08em] text-slate-500">
          {title}
        </p>
        {description && (
          <p className="text-sm text-slate-500">{description}</p>
        )}
      </div>
    </div>
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>
  </div>
);

const Field: React.FC<FieldProps & { value?: any; onChange: any }> = ({
  name,
  label,
  icon: Icon,
  type = "text",
  placeholder,
  highlighted = false,
  tooltip,
  value,
  onChange,
}) => (
  <label
    className={`block rounded-xl text-sm font-medium text-slate-700 ${
      highlighted ? "bg-blue-50/70 p-3 ring-1 ring-blue-200" : ""
    }`}
  >
    <span
      className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wide ${
        highlighted ? "text-blue-700" : "text-slate-500"
      }`}
    >
      <Icon className="h-4 w-4 text-slate-400" />
      {label}
      {tooltip && (
        <span className="group relative inline-flex items-center">
          <CircleHelp className="h-4 w-4 cursor-help text-blue-500" />
          <span className="pointer-events-none absolute left-1/2 top-[calc(100%+0.5rem)] z-20 w-64 -translate-x-1/2 rounded-lg bg-slate-900 px-3 py-2 text-center text-[11px] normal-case tracking-normal text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
            {tooltip}
          </span>
        </span>
      )}
    </span>
    {type === "checkbox" ? (
      <div className="mt-3 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm">
        <input
          type="checkbox"
          name={name}
          checked={!!value}
          onChange={onChange}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500"
        />
        <span className="text-sm text-slate-700">Sí</span>
      </div>
    ) : (
      <input
        type={type}
        name={name}
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        className={`${inputBase} ${
          highlighted
            ? "border-blue-300 bg-blue-50/30 focus:border-blue-600 focus:ring-blue-200"
            : ""
        }`}
      />
    )}
  </label>
);

export default function EditarSocioManual() {
  const [socio, setSocio] = useState<Socio>({} as Socio);
  const [logoComercioFile, setLogoComercioFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [categoriasDisponibles, setCategoriasDisponibles] = useState<CategoriaItem[]>([]);
  const [categoriaIds, setCategoriaIds] = useState<number[]>([]);
  const [categoriasOriginales, setCategoriasOriginales] = useState<number[]>([]);
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSocio = async () => {
      try {
        const cached = localStorage.getItem("socioSeleccionado");
        if (!cached) {
          navigate("/seleccionar-socio");
          return;
        }
        const { socio_id } = JSON.parse(cached);
        const socioData = await socioService.getSocioById(
          socio_id,
          getAccessTokenSilently
        );
        setSocio({
          ...socioData,
          socio_firma: socioData.socio_firma || socioData.logoUrl || "",
        });
        try {
          const token = await getAccessTokenSilently();
          const [cats, catsSocio] = await Promise.all([
            obtenerCategorias(token),
            obtenerCategoriasDeSocio(socio_id, token),
          ]);
          const idsIniciales = catsSocio
            .map((c) => c.categoria_id ?? c.id!)
            .filter((x): x is number => typeof x === "number");
          setCategoriasDisponibles(cats);
          setCategoriaIds(idsIniciales);
          setCategoriasOriginales(idsIniciales);
        } catch (errCat) {
          console.error("Error al cargar categorías:", errCat);
        }
      } catch (err) {
        console.error("Error al cargar socio:", err);
        setError("Error al cargar los datos del socio.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchSocio();
  }, [navigate, getAccessTokenSilently]);

  useEffect(() => {
    return () => {
      if (logoPreview?.startsWith("blob:")) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked =
      type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : undefined;
    setSocio((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleRubroToggle = (id: number) => {
    setSocio((prev) => {
      const actuales = getRubrosIds(
        Number(prev.socio_rubro) || null,
        prev.socio_rubros || null
      );
      const index = actuales.indexOf(id);
      const nuevos =
        index >= 0 ? actuales.filter((x) => x !== id) : [...actuales, id];
      return {
        ...prev,
        socio_rubro: (nuevos.length ? nuevos[0] : null) as unknown as number,
        socio_rubros: nuevos.length > 1 ? nuevos.slice(1).join(",") : "",
      };
    });
  };

  const handleCategoriaToggle = (id: number) => {
    setCategoriaIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      const numOrNull = (v: any): number | null => {
        if (v === null || v === undefined || String(v).trim() === "") return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      };

      const rubrosSeleccionados = getRubrosIds(
        Number(socio.socio_rubro) || null,
        socio.socio_rubros || null
      );

      const fechaNacimientoParaGuardar = (() => {
        const raw = String(socio.socio_fechaNacimiento ?? "").trim();
        if (!raw) return null;
        return raw.length >= 10 ? raw.slice(0, 10) : raw;
      })();

      const socioToSave: Record<string, any> = {
        socio_nombre: socio.socio_nombre,
        socio_mail: socio.socio_mail,
        socio_dni: socio.socio_dni,
        socio_nacionalidad: socio.socio_nacionalidad,
        socio_fechaNacimiento: fechaNacimientoParaGuardar,
        socio_firma: socio.socio_firma,
        socio_domicilio: socio.socio_domicilio,
        socio_localidad: numOrNull(socio.socio_localidad),
        socio_localidad_nombre: (socio.socio_localidad_nombre || "").trim() || null,
        socio_celular: socio.socio_celular,
        socio_telefono: socio.socio_telefono,
        socio_otroTelefono: socio.socio_otroTelefono,
        socio_cuit: socio.socio_cuit,
        socio_tipoEmpresa: socio.socio_tipoEmpresa,
        socio_tipoSocio: (() => {
          const t = String(socio.socio_tipoSocio ?? "").trim();
          if (t === "") return null;
          const n = Number(t);
          return Number.isFinite(n) ? n : t;
        })(),
        socio_segmento: numOrNull(socio.socio_segmento),
        socio_gestion: numOrNull(socio.socio_gestion),
        socio_Campanias: numOrNull(socio.socio_Campanias ?? socio.socio_campanias),
        socio_acta: numOrNull(socio.socio_acta),
        socio_estado: numOrNull(socio.socio_estado),
        socio_rubro: rubrosSeleccionados.length ? rubrosSeleccionados[0] : null,
        socio_rubros:
          rubrosSeleccionados.length > 1
            ? rubrosSeleccionados.slice(1).join(",")
            : null,
        socio_tieneCajaSeguridad: Boolean(socio.socio_tieneCajaSeguridad),
      };

      if (logoComercioFile) {
        const logoUrl = await socioService.uploadSocioLogo(
          socio.socio_id,
          logoComercioFile,
          getAccessTokenSilently
        );
        if (logoPreview?.startsWith("blob:")) {
          URL.revokeObjectURL(logoPreview);
        }
        const logoUrlFresh = `${logoUrl}${logoUrl.includes("?") ? "&" : "?"}t=${Date.now()}`;
        setLogoPreview(logoUrlFresh);
        socioToSave.logoUrl = logoUrlFresh;
        socioToSave.socio_firma = logoUrlFresh;
        setSocio((prev) => ({
          ...prev,
          logoUrl: logoUrlFresh,
          socio_firma: logoUrlFresh,
        }));
      }

      const socioActualizado = await socioService.updateSocio(
        socio.socio_id,
        socioToSave as Partial<Socio>,
        getAccessTokenSilently
      );

      setSocio((prev) => ({
        ...prev,
        ...socioActualizado,
        ...(socioToSave.logoUrl
          ? { logoUrl: socioToSave.logoUrl }
          : {}),
      }));

      // Refrescar la copia en localStorage para que el resto de la app vea los cambios.
      try {
        const refreshed = await socioService.getSocioById(
          socio.socio_id,
          getAccessTokenSilently
        );
        socioService.saveSelectedSocioInLocalStorage({
          ...refreshed,
          ...(socioToSave.logoUrl ? { logoUrl: socioToSave.logoUrl } : {}),
        } as Socio);
      } catch (e) {
        console.warn("No se pudo refrescar el socio en localStorage:", e);
      }

      const token = await getAccessTokenSilently();
      const paraAgregar = categoriaIds.filter((id) => !categoriasOriginales.includes(id));
      const paraQuitar = categoriasOriginales.filter((id) => !categoriaIds.includes(id));
      for (const id of paraAgregar) {
        await asignarSocioACategoria(id, socio.socio_id, token);
      }
      for (const id of paraQuitar) {
        await quitarSocioDeCategoria(id, socio.socio_id, token);
      }
      setCategoriasOriginales(categoriaIds);
      setLogoComercioFile(null);
      setToast({ type: "success", message: "Socio actualizado con éxito." });
      setTimeout(() => setToast(null), 3000);
    } catch (err: any) {
      console.error("Error al guardar:", err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Hubo un error al guardar los cambios.";
      setToast({ type: "error", message: msg });
      setTimeout(() => setToast(null), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (logoPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(logoPreview);
    }

    setLogoComercioFile(selected);
    setLogoPreview(URL.createObjectURL(selected));
  };

  if (isLoading) return <Loading message="Cargando datos del socio" detail="Un momento, por favor..." />;
  if (error) return <p className="text-red-600">{error}</p>;

  const logoExistente = socio.logoUrl || socio.socio_firma || socio.socio_logoComercio || socio.socio_logo || null;
  const logoMostrado = logoPreview || logoExistente;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      {toast && (
        <div className="fixed left-1/2 top-6 z-50 -translate-x-1/2 transform">
          <div
            className={`flex items-center gap-3 rounded-2xl px-4 py-3 shadow-xl ring-1 ring-black/10 ${
              toast.type === "success"
                ? "bg-emerald-50 text-emerald-800 ring-emerald-100"
                : "bg-rose-50 text-rose-800 ring-rose-100"
            }`}
          >
            <div
              className={`h-2 w-2 rounded-full ${
                toast.type === "success" ? "bg-emerald-500" : "bg-rose-500"
              }`}
            />
            <p className="text-sm font-medium">{toast.message}</p>
            <button
              onClick={() => setToast(null)}
              className="ml-2 text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-700"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
      <div className="rounded-3xl bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 p-6 text-white shadow-xl">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="space-y-1">
            <p className="text-xs uppercase tracking-[0.12em] text-blue-100">
              Socio #{socio.socio_id ?? "—"}
            </p>
            <h1 className="text-3xl font-semibold leading-tight">
              {socio.socio_nombre || "Nombre pendiente"}
            </h1>
            <p className="text-sm text-blue-100">{socio.socio_mail || "Sin domicilio electrónico"}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="rounded-full bg-white/15 px-3 py-1">
              {getNombreSegmento(Number(socio.socio_segmento) || 0) || "Segmento sin asignar"}
            </span>
            <span className="rounded-full bg-white/15 px-3 py-1">
              {getRubrosNombres(Number(socio.socio_rubro) || null, socio.socio_rubros) || "Rubro sin asignar"}
            </span>
            <span className="rounded-full bg-white/15 px-3 py-1 flex items-center gap-1">
              <CheckSquare className="h-4 w-4" />
              {socio.socio_tieneCajaSeguridad ? "Caja de seguridad" : "Sin caja"}
            </span>
            <span className="rounded-full bg-white/15 px-3 py-1 flex items-center gap-1">
              <Tag className="h-4 w-4" />
              {categoriaIds.length} categoría{categoriaIds.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
        {isSaving && (
          <div className="mt-4 flex items-center gap-2 rounded-xl bg-white/15 px-3 py-2 text-sm text-blue-50 backdrop-blur">
            <Loader2 className="h-4 w-4 animate-spin" />
            Guardando cambios...
          </div>
        )}
      </div>

      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-xl">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.08em] text-slate-500">
              Editor de Socio
            </p>
            <h2 className="text-2xl font-semibold text-slate-900">
              Actualizá los datos del socio
            </h2>
            <p className="text-sm text-slate-500">
              Los cambios se guardan en el perfil del socio y afectan al panel administrativo.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
              title="Volver"
            >
              <ArrowLeft className="h-4 w-4" />
              Volver
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition focus:ring-2 focus:ring-blue-200 ${
                isSaving
                  ? "cursor-not-allowed bg-blue-400"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
              {isSaving ? "Guardando..." : "Guardar cambios"}
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <Section title="Identidad" description="Datos de identificación y contacto directo.">
            <Field
              name="socio_nombre"
              label="Nombre"
              icon={User}
              value={socio.socio_nombre}
              onChange={handleChange}
            />
            <Field
              name="socio_mail"
              label="Domicilio electrónico"
              icon={Mail}
              type="email"
              highlighted
              tooltip="Es el medio por el cual se le va a informar de cualquier cosa importante."
              value={socio.socio_mail}
              onChange={handleChange}
            />
            <Field
              name="socio_dni"
              label="DNI"
              icon={CreditCard}
              value={socio.socio_dni}
              onChange={handleChange}
            />
            <Field
              name="socio_fechaNacimiento"
              label="Fecha de nacimiento"
              icon={Calendar}
              type="date"
              value={socio.socio_fechaNacimiento}
              onChange={handleChange}
            />
            <Field
              name="socio_nacionalidad"
              label="Nacionalidad"
              icon={Flag}
              value={socio.socio_nacionalidad}
              onChange={handleChange}
            />
            <Field
              name="socio_firma"
              label="Firma"
              icon={Briefcase}
              value={socio.socio_firma}
              onChange={handleChange}
            />
          </Section>

          <Section title="Ubicación y contacto" description="Dirección y teléfonos asociados.">
            <Field
              name="socio_domicilio"
              label="Domicilio"
              icon={MapPin}
              value={socio.socio_domicilio}
              onChange={handleChange}
            />
            <Field
              name="socio_localidad_nombre"
              label="Localidad"
              icon={MapPin}
              value={socio.socio_localidad_nombre}
              onChange={handleChange}
            />
            <Field
              name="socio_celular"
              label="Celular"
              icon={Phone}
              value={socio.socio_celular}
              onChange={handleChange}
            />
            <Field
              name="socio_telefono"
              label="Teléfono"
              icon={Phone}
              value={socio.socio_telefono}
              onChange={handleChange}
            />
            <Field
              name="socio_otroTelefono"
              label="Otro teléfono"
              icon={Phone}
              value={socio.socio_otroTelefono}
              onChange={handleChange}
            />
          </Section>

          <Section title="Datos fiscales y empresa" description="Información legal y segmentación.">
            <Field
              name="socio_cuit"
              label="CUIT"
              icon={Hash}
              value={socio.socio_cuit}
              onChange={handleChange}
            />
            <Field
              name="socio_tipoEmpresa"
              label="Tipo de empresa"
              icon={Building}
              value={socio.socio_tipoEmpresa}
              onChange={handleChange}
            />
            <label className="block text-sm font-medium text-slate-700">
              <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <User className="h-4 w-4 text-slate-400" />
                Tipo de socio
              </span>
              <select
                name="socio_tipoSocio"
                value={String(socio.socio_tipoSocio || "")}
                onChange={handleChange}
                className={inputBase}
              >
                <option value="">Seleccionar tipo...</option>
                {Object.entries(TipoSocioNombre).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block text-sm font-medium text-slate-700">
              <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <Layers className="h-4 w-4 text-slate-400" />
                Rubro
              </span>
              <div className="mt-3 flex flex-wrap gap-2">
                {Object.values(Rubro)
                  .filter((v): v is number => typeof v === "number")
                  .map((val) => {
                    const checked = getRubrosIds(
                      Number(socio.socio_rubro) || null,
                      socio.socio_rubros || null
                    ).includes(val);
                    return (
                      <label
                        key={val}
                        className={`flex cursor-pointer select-none items-center gap-2 rounded-lg border px-3 py-2 text-sm shadow-sm transition ${
                          checked
                            ? "border-blue-500 bg-blue-50 text-blue-700"
                            : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleRubroToggle(val)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                        />
                        {getNombreRubro(val)}
                      </label>
                    );
                  })}
              </div>
            </label>

            <label className="block text-sm font-medium text-slate-700">
              <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <Tag className="h-4 w-4 text-slate-400" />
                Segmento
              </span>
              <select
                name="socio_segmento"
                value={String(socio.socio_segmento || "")}
                onChange={handleChange}
                className={inputBase}
              >
                <option value="">Seleccionar segmento...</option>
                {Object.entries(Segmento).map(([key, val]) => (
                  <option key={key} value={val}>
                    {getNombreSegmento(val as unknown as number)}
                  </option>
                ))}
              </select>
            </label>
          </Section>

          <Section
            title="Categorías"
            description="Agrupá al socio para asignarle servicios de forma masiva."
          >
            <div className="md:col-span-2">
              {categoriasDisponibles.length === 0 ? (
                <p className="text-sm text-slate-500">
                  Aún no hay categorías creadas. Creá categorías y asigná servicios desde el
                  panel administrativo.
                </p>
              ) : (
                <div className="mt-2 flex flex-wrap gap-2">
                  {categoriasDisponibles.map((cat) => {
                    const id = cat.categoria_id ?? cat.id!;
                    const checked = categoriaIds.includes(id);
                    return (
                      <label
                        key={id}
                        className={`flex cursor-pointer select-none items-center gap-2 rounded-lg border px-3 py-2 text-sm shadow-sm transition ${
                          checked
                            ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                            : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleCategoriaToggle(id)}
                          className="h-4 w-4 text-emerald-600 focus:ring-emerald-500"
                        />
                        <Tag className="h-4 w-4" />
                        {cat.nombre}
                      </label>
                    );
                  })}
                </div>
              )}
              <p className="mt-2 text-xs text-slate-500">
                {categoriaIds.length} categoría{categoriaIds.length !== 1 ? "s" : ""} seleccionada
                {categoriaIds.length !== 1 ? "s" : ""}.
              </p>
            </div>
          </Section>

          <Section title="Marca del comercio" description="Subí el logo para usarlo en la identidad del comercio.">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700">
                <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <ImagePlus className="h-4 w-4 text-slate-400" />
                  Logo del comercio
                </span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className={`${inputBase} file:mr-4 file:rounded-md file:border-0 file:bg-blue-600 file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-white hover:file:bg-blue-700`}
                />
              </label>
              <p className="mt-2 text-xs text-slate-500">
                Formatos sugeridos: PNG o JPG. Si seleccionás una imagen nueva, se reemplaza la anterior.
              </p>
              {logoMostrado && (
                <div className="mt-4 inline-flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                  <img
                    src={logoMostrado}
                    alt="Logo del comercio"
                    className="h-16 w-16 rounded-lg border border-slate-200 object-contain bg-white"
                  />
                  <div className="text-xs text-slate-600">
                    <p className="font-semibold text-slate-700">
                      {logoComercioFile ? "Logo nuevo seleccionado" : "Logo actual"}
                    </p>
                    <p>{logoComercioFile?.name || "Imagen guardada en el perfil"}</p>
                    <p className="mt-1 max-w-[24rem] break-all text-[11px] text-slate-500">
                      URL: {logoMostrado}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </Section>

          <Section title="Administrativo" description="Seguimiento interno del socio.">
            <Field
              name="socio_gestion"
              label="Gestión"
              icon={FileText}
              value={socio.socio_gestion}
              onChange={handleChange}
            />
            <Field
              name="socio_Campanias"
              label="Campañas"
              icon={FileText}
              value={socio.socio_Campanias ?? socio.socio_campanias}
              onChange={handleChange}
            />
            <Field
              name="socio_acta"
              label="Acta"
              icon={FileText}
              type="number"
              value={socio.socio_acta}
              onChange={handleChange}
            />
            <Field
              name="socio_estado"
              label="Estado"
              icon={FileText}
              type="number"
              value={socio.socio_estado}
              onChange={handleChange}
            />
            <Field
              name="socio_tieneCajaSeguridad"
              label="Tiene caja de seguridad"
              icon={CheckSquare}
              type="checkbox"
              value={socio.socio_tieneCajaSeguridad}
              onChange={handleChange}
            />
          </Section>
        </div>
      </div>
    </div>
  );
}
